/**
 * Named slots on the Modify Quote policy card (#36357). Each slot is empty until a ticket in the
 * Policy cards lane (#36398) fills it from its own folder, so those tickets don't edit the card and can
 * merge in any order.
 *
 * To fill a slot, add a `*.policy-card-slot.jsx` file anywhere under `policy-cards/`. It exports:
 *   - `slot`: one of the `PolicyCardSlot` values below
 *   - a default-exported component, rendered once per policy with `{ policy }` (see `policy-lines.js`).
 *     Return null for policies it doesn't apply to (the mortgage lender only shows on escrow, say).
 * Several files can fill the same slot; they render in file path order. `slots.test.js` checks every
 * fill file's shape in CI.
 *
 * `billingDay` and `paymentScheduleLink` sit inside the price details box. The billing day fill renders
 * below the payments row in the same section, so it draws its own rule above it (Figma "Custom billing
 * day"); the payment schedule link gets its own section, and the box draws the rule.
 */
export const PolicyCardSlot = Object.freeze({
  EligibilityIssues: 'eligibilityIssues', // #36372
  Callouts: 'callouts',
  EffectiveDate: 'effectiveDate', // #36358
  BillingDetails: 'billingDetails', // #36359
  BillingDay: 'billingDay', // #36361
  PaymentScheduleLink: 'paymentScheduleLink', // #36360
  MortgageLender: 'mortgageLender' // #36362
});

const slotNames = new Set(Object.values(PolicyCardSlot));

// A component is a function, or an object for wrapped ones (`observer`, `memo`, `forwardRef`).
const isComponent = (component) =>
  typeof component === 'function' || (typeof component === 'object' && component !== null);

export const isValidSlotFill = (fillModule) => slotNames.has(fillModule?.slot) && isComponent(fillModule?.default);

// Groups fill modules ({ [path]: module }) by slot, in path order, dropping any that don't match the
// contract above (slots.test.js names them).
export const buildSlotFills = (fillModules) =>
  Object.keys(fillModules)
    .sort()
    .map((path) => fillModules[path])
    .filter(isValidSlotFill)
    .reduce((fills, fillModule) => {
      fills[fillModule.slot] = [...(fills[fillModule.slot] ?? []), fillModule.default];
      return fills;
    }, {});
